import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { StoreState } from "../../store/store.interface";
import { updateHasStatesLoadedForSelectedCountry } from "./country";
import { FetchStateRequest, CountryStateSelectState } from "./country-state.interface";
import { PAYMENT_MESSAGE_ID, updateGlobalMessage } from "../alert/global-message";
import { GENERIC_TRANSACTION_ERROR, defaultApiState } from "../../common/constants/api-constants";
import { MessageType } from "../alert/global-message.interface";
import { ActionStatus } from "../../common/constants/action-status";
import environment from "../../common/environment";
import { getResponseData } from "../../common/utils/api-utils";
import { getCustomerToken } from "../customer/customer";
import store from "../../store/store";
import { ShopApi } from "../../common/constants/shop-api";
import { CountryMagentoResponse } from "./country.interface";
import { mapCountryStateMagentoResponse } from "../address/mapping/country-state.mapping";

/*----- DECLARE THUNKS ------*/

export const fetchStateOptions = createAsyncThunk('countryState/fetchStateOptions', async (data: FetchStateRequest, thunkAPI) => {
  try{
    return await getStateList(data.countryCode).then(response => {
      thunkAPI.dispatch(updateHasStatesLoadedForSelectedCountry({id: data.type, hasStatesLoaded: true}));
      return response;
    });
  } catch(error) {
    thunkAPI.dispatch(updateGlobalMessage({
      id: PAYMENT_MESSAGE_ID,
      message: GENERIC_TRANSACTION_ERROR,
      type: MessageType.primary
    }));
    return thunkAPI.rejectWithValue(error);
  }
},{
  condition: ((_data, {getState} )=>{
    const state = getState() as StoreState;
    if(state.countryStateSelectState.countryStateResponse.status === ActionStatus.loading || state.countryStateSelectState.countryStateResponse.status === ActionStatus.succeeded){
      return false;
    }
    return true;
  })
});

/*----- CREATE INITIAL STATE ------*/

export const countryStateSelectInitialState: CountryStateSelectState = {
  selectedCountryState: {},
  countryStateResponse: defaultApiState,
};

/*----- CREATE STORE SLICE ------*/

export const countryStateSlice = createSlice({
  name:"countryState",
  initialState: countryStateSelectInitialState,
  reducers:{
    updateSelectedCountryState(state, action) {
      state.selectedCountryState[action.payload.id]= action.payload.state;
    },
    updateCountryStateStatus(state, action) {
      state.countryStateResponse.status = action.payload;
    },
    resetStateResponse(state) {
      state.selectedCountryState = {};
      state.countryStateResponse = defaultApiState;
    }
  },
  extraReducers(builder) {
    builder
    .addCase(fetchStateOptions.pending, (state) => {
      state.countryStateResponse.status = ActionStatus.loading;
    })
    .addCase(fetchStateOptions.fulfilled, (state, action) => {
      state.countryStateResponse.status = ActionStatus.succeeded;
      state.countryStateResponse.data = action.payload;
    })
    .addCase(fetchStateOptions.rejected, (state, action) => {
      state.countryStateResponse.status = ActionStatus.failed;
      state.countryStateResponse.error = action.payload;
    })
  }
});

/*----- CREATE SLICE ACTIONS ------*/

export const { updateSelectedCountryState, updateCountryStateStatus, resetStateResponse } = countryStateSlice.actions

/*----- CREATE SELECTORS ------*/

export const getCountryStateOptions = (state: StoreState) =>
  state.countryStateSelectState.countryStateResponse.data;

export const getCountryStateStatus = (state: StoreState) =>
  state.countryStateSelectState.countryStateResponse.status;

export const getSelectedCountryState = (id: string, state: StoreState) =>
  state.countryStateSelectState.selectedCountryState[id];

export const getCountryStateError = (state: StoreState) =>
  state.countryStateSelectState.countryStateResponse.error;
  
/*----- CREATE REDUCER ------*/

export const countryStateSelectReducer = countryStateSlice.reducer;

/*----- API CALLS ------*/

async function getStateList(countryCode: string) {
  return await getStateListMagentoApiCall(countryCode);
}

async function getStateListMagentoApiCall(countryCode: string) {
  const token = getCustomerToken(store.getState() as StoreState);
  const url = new URL(`${environment().magentoUrl}${ShopApi.countriesMagento}/${countryCode}`);
  const response: CountryMagentoResponse = await getResponseData(
    new Request(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
  );
  return mapCountryStateMagentoResponse(response);
}

// Utils

export const countryHasStates = (countryCode) => ['US', 'CA'].indexOf(countryCode) != -1; 